import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Alert, InputGroup, Form, Button, Collapse, Badge } from 'react-bootstrap';
import Header from './Header';
import CategoryCards from './CategoryCards';
import CategoryDetail from './CategoryDetail';
import PageStats from './PageStats';
import { apiService } from '../services/api';
import '../styles/Dashboard.css';
import '../styles/CategoryDetail.css';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryDetail, setShowCategoryDetail] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all'); // Can be 'all', 'category', or 'brand'
  const [validationFilter, setValidationFilter] = useState('');
  const [dateRange, setDateRange] = useState({
    fromDate: '',
    toDate: ''
  });
  const [dateError, setDateError] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false); 
  const [searchResults, setSearchResults] = useState({
    categories: [],
    brands: []
  });
  const [isSearching, setIsSearching] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    onlyMissingChannels: false,
    nameQuery: '',
  });

  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch category mentions data
        const response = await apiService.fetchCategoryMentions();
        if (mounted) {
          setCategoryData(response);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load dashboard data');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    
    return () => {
      mounted = false;
    };
  }, []);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setShowCategoryDetail(true);
  };

  const handleCloseDetail = () => {
    setShowCategoryDetail(false);
  };

  // Modified function to handle search with validation filter
  const handleSearch = async (query, type, filter, dateParams) => {
    if (!query.trim() && !dateParams.fromDate && !dateParams.toDate) {
      // If both search query and date range are empty, reset to showing all categories
      setSearchResults({ categories: [], brands: [] });
      return;
    }

    // Validate date range if provided
    if (dateParams.fromDate && dateParams.toDate) {
      const startDate = new Date(dateParams.fromDate);
      const endDate = new Date(dateParams.toDate);
      
      if (endDate < startDate) {
        setDateError('End date must be after start date');
        return;
      }
    } else if ((dateParams.fromDate && !dateParams.toDate) || (!dateParams.fromDate && dateParams.toDate)) {
      setDateError('Both start and end dates are required');
      return;
    }

    try {
      setIsSearching(true);
      const results = await apiService.fetchSearchResults(query, type, filter, dateParams);
      setSearchResults(results.results);
      setDateError(''); // Clear error on successful search
    } catch (err) {
      setError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Fix the debounced search function implementation
  const debouncedSearchRef = React.useRef();

  useEffect(() => {
    // Setup debounced search function
    debouncedSearchRef.current = debounce((query, type, filter, dateParams) => {
      // Pass only query and type for now
      handleSearch(query, type, filter, dateParams);
    }, 300);
    
    return () => {
      // Clean up any pending debounced calls
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel && debouncedSearchRef.current.cancel();
      }
    };
  }, []);

  // Handle search input changes
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current(query, searchType, validationFilter, dateRange);
    }
  };

  // Handle search type changes
  const handleSearchTypeChange = (e) => {
    const type = e.target.value;
    setSearchType(type);
    
    if ((searchQuery.trim() || dateRange.fromDate || dateRange.toDate) && debouncedSearchRef.current) {
      debouncedSearchRef.current(searchQuery, type, validationFilter, dateRange);
    }
  };

  // Handle date range changes (now just updates state without triggering search)
  const handleDateChange = (field, value) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);
    
    // Validate dates when both are present
    if (newDateRange.fromDate && newDateRange.toDate) {
      const startDate = new Date(newDateRange.fromDate);
      const endDate = new Date(newDateRange.toDate);
      
      if (endDate < startDate) {
        setDateError('End date must be after start date');
      } else {
        setDateError('');
      }
    }
  };

  // Clear date filters
  const handleClearDateFilter = () => {
    setDateRange({ fromDate: '', toDate: '' });
    
    if (searchQuery.trim() && debouncedSearchRef.current) {
      debouncedSearchRef.current(searchQuery, searchType, validationFilter, { fromDate: '', toDate: '' });
    }
  };

  // Apply date filter explicitly when button is clicked
  const handleApplyDateFilter = () => {
    if (dateRange.fromDate && dateRange.toDate) {
      const startDate = new Date(dateRange.fromDate);
      const endDate = new Date(dateRange.toDate);
      
      if (endDate < startDate) {
        setDateError('End date must be after start date');
        return;
      }
    } else if ((dateRange.fromDate && !dateRange.toDate) || (!dateRange.fromDate && dateRange.toDate)) {
      setDateError('Both start and end dates are required');
      return;
    }
    
    if (searchQuery || (dateRange.fromDate && dateRange.toDate)) {
      handleSearch(searchQuery, searchType, validationFilter, dateRange);
    }
    setShowDateFilter(false);
  };

  // Properly implemented debounce function
  function debounce(func, wait) {
    let timeout;
    
    const debouncedFn = function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
    
    debouncedFn.cancel = function() {
      clearTimeout(timeout);
    };
    
    return debouncedFn;
  }

  // Determine what data to display based on search results
  const displayedCategories = React.useMemo(() => {
    if (!searchQuery.trim()) return categoryData;
    
    if (searchResults.categories && searchResults.categories.length > 0) {
      // If we have category results, filter the full category data to only show matching categories
      return categoryData.filter(category => 
        searchResults.categories.some(result => result.id === category.categoryid)
      );
    }
    
    if (searchResults.brands && searchResults.brands.length > 0) {
      // If we only have brand results, filter to show categories that contain matching brands
      return categoryData.filter(category => 
        searchResults.brands.some(brand => brand.category_id === category.categoryid)
      );
    }
    
    return [];
  }, [categoryData, searchResults, searchQuery]);

  // Handle sort change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };
  
  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Apply sort to displayed categories
  const sortedCategories = React.useMemo(() => {
    let sorted = [...displayedCategories];
    
    switch (sortBy) {
      case 'name-asc':
        sorted.sort((a, b) => a.category_name.localeCompare(b.category_name));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.category_name.localeCompare(a.category_name));
        break;
      case 'brands-desc':
        sorted.sort((a, b) => b.totalbrands - a.totalbrands);
        break;
      case 'accounts-desc':
        sorted.sort((a, b) => b.totalaccounts - a.totalaccounts);
        break;
      case 'posts-desc':
        sorted.sort((a, b) => b.brandpost - a.brandpost);
        break;
      case 'comments-desc':
        sorted.sort((a, b) => b.comments - a.comments);
        break;
      case 'outdated':
        sorted.sort((a, b) => b.is_outdated - a.is_outdated);
        break;
      default:
        // Default sorting (no change)
        break;
    }
    
    // Apply filter for missing channels if enabled
    if (filters.onlyMissingChannels) {
      sorted = sorted.filter(category => 
        category.brands_missing_accounts && category.brands_missing_accounts.length > 0
      );
    }
    
    // Apply name filter if provided
    if (filters.nameQuery.trim()) {
      const query = filters.nameQuery.toLowerCase();
      sorted = sorted.filter(category => 
        category.category_name.toLowerCase().includes(query)
      );
    }
    
    return sorted;
  }, [displayedCategories, sortBy, filters]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* <Header /> */}
      <Container fluid>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Row className="mb-4">
          <Col md={12}>
            <div className="search-container">
              <InputGroup className="dashboard-search mb-2">
                <InputGroup.Text id="search-addon">
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search categories and brands..."
                  aria-label="Search categories and brands"
                  aria-describedby="search-addon"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                />
                <InputGroup.Text as="label" htmlFor="search-type-select">
                  <span className="me-2">Search in:</span>
                  <Form.Select 
                    id="search-type-select"
                    size="sm"
                    value={searchType}
                    onChange={handleSearchTypeChange}
                    style={{ border: 'none', width: '95px', background: 'transparent' }}
                  >
                    <option value="all">All</option>
                    <option value="category">Categories</option>
                    <option value="brand">Brands</option>
                  </Form.Select>
                </InputGroup.Text>
                
                <InputGroup.Text 
                  onClick={() => setShowFilters(!showFilters)}
                  className={`filter-toggle ${showFilters ? 'active' : ''}`}
                >
                  <i className={`fas fa-sliders-h ${showFilters ? 'text-primary' : ''}`}></i>
                </InputGroup.Text>
                
                {(searchQuery || dateRange.fromDate || dateRange.toDate) && (
                  <InputGroup.Text 
                    className="clear-search" 
                    onClick={() => {
                      setSearchQuery('');
                      setDateRange({ fromDate: '', toDate: '' });
                      setSearchResults({ categories: [], brands: [] });
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </InputGroup.Text>
                )}
              </InputGroup>
              
              {/* Filter and sort panel */}
              <Collapse in={showFilters}>
                <div className="filter-sort-panel">
                  <div className="filter-header">
                    <h6><i className="fas fa-filter me-2"></i> Advanced Filters</h6>
                    <Button 
                      variant="link" 
                      className="close-btn"
                      onClick={() => setShowFilters(false)}
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </div>
                  
                  <div className="filter-content">
                    <Row>
                      <Col md={4} className="mb-3 mb-md-0">
                        <div className="filter-group">
                          <label className="filter-label">Sort By</label>
                          <Form.Select 
                            value={sortBy} 
                            onChange={handleSortChange}
                            className="sort-select"
                          >
                            <option value="default">Default</option>
                            <option value="name-asc">Name (A-Z)</option>
                            <option value="name-desc">Name (Z-A)</option>
                            <option value="brands-desc">Most Brands</option>
                            <option value="accounts-desc">Most Accounts</option>
                            <option value="posts-desc">Most Posts</option>
                            <option value="comments-desc">Most Comments</option>
                            <option value="outdated">Outdated First</option>
                          </Form.Select>
                        </div>
                      </Col>
                      
                      <Col md={4} className="mb-3 mb-md-0">
                        <div className="filter-group">
                          <label className="filter-label">Filter by Name</label>
                          <InputGroup>
                            <InputGroup.Text>
                              <i className="fas fa-font"></i>
                            </InputGroup.Text>
                            <Form.Control
                              placeholder="Filter by name..."
                              value={filters.nameQuery}
                              onChange={(e) => handleFilterChange('nameQuery', e.target.value)}
                            />
                            {filters.nameQuery && (
                              <Button 
                                variant="link"
                                className="clear-name-filter"
                                onClick={() => handleFilterChange('nameQuery', '')}
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            )}
                          </InputGroup>
                        </div>
                      </Col>
                      
                      <Col md={4} className="missing-channels-filter">
                        <div className="filter-group">
                          <label className="filter-label mb-2">Show Only</label>
                          <div className="form-check custom-checkbox">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="missingChannelsFilter"
                              checked={filters.onlyMissingChannels}
                              onChange={(e) => handleFilterChange('onlyMissingChannels', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="missingChannelsFilter">
                              Categories with Missing Channels
                            </label>
                          </div>
                        </div>
                      </Col>
                    </Row>
                    
                    {(filters.onlyMissingChannels || filters.nameQuery) && (
                      <div className="active-filters mt-3">
                        <div className="active-filters-header">
                          <small>Active Filters:</small>
                        </div>
                        <div className="active-filters-badges">
                          {filters.onlyMissingChannels && (
                            <Badge 
                              bg="primary" 
                              className="filter-badge"
                              onClick={() => handleFilterChange('onlyMissingChannels', false)}
                            >
                              Missing Channels <i className="fas fa-times ms-1"></i>
                            </Badge>
                          )}
                          {filters.nameQuery && (
                            <Badge 
                              bg="primary" 
                              className="filter-badge"
                              onClick={() => handleFilterChange('nameQuery', '')}
                            >
                              Name: {filters.nameQuery} <i className="fas fa-times ms-1"></i>
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Collapse>
              
              {/* Keep the Collapse component but it will stay hidden because showDateFilter is false */}
              <Collapse in={showDateFilter}>
                <div className="date-filter-panel">
                  <div className="filter-header">
                    <h6><i className="fas fa-calendar-alt me-2"></i> Date Range Filter</h6>
                    <Button 
                      variant="link" 
                      className="close-btn"
                      onClick={() => setShowDateFilter(false)}
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </div>
                  
                  <div className="filter-content">
                    <div className="date-picker-container">
                      <div className="date-input-block">
                        <label className="date-label">Start Date</label>
                        <div className="date-input-wrapper">
                          <i className="fas fa-calendar-day input-icon"></i>
                          <input 
                            type="date" 
                            value={dateRange.fromDate}
                            onChange={(e) => handleDateChange('fromDate', e.target.value)}
                            className={`custom-date-input ${dateError ? 'is-invalid' : ''}`}
                          />
                        </div>
                      </div>
                      
                      <div className="date-range-divider">
                        <div className="divider-line"></div>
                        <div className="divider-icon"><i className="fas fa-arrow-right"></i></div>
                        <div className="divider-line"></div>
                      </div>
                      
                      <div className="date-input-block">
                        <label className="date-label">End Date</label>
                        <div className="date-input-wrapper">
                          <i className="fas fa-calendar-day input-icon"></i>
                          <input 
                            type="date" 
                            value={dateRange.toDate}
                            onChange={(e) => handleDateChange('toDate', e.target.value)}
                            className={`custom-date-input ${dateError ? 'is-invalid' : ''}`}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {dateError && (
                      <div className="filter-error">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {dateError}
                      </div>
                    )}
                    
                    {dateRange.fromDate && dateRange.toDate && !dateError && (
                      <div className="filter-preview">
                        <i className="fas fa-info-circle me-2"></i>
                        <span>Filter data from <strong>{new Date(dateRange.fromDate).toLocaleDateString()}</strong> to <strong>{new Date(dateRange.toDate).toLocaleDateString()}</strong></span>
                      </div>
                    )}
                    
                    <div className="filter-actions">
                      <Button 
                        variant="outline-secondary" 
                        className="action-btn"
                        onClick={handleClearDateFilter}
                        disabled={!dateRange.fromDate && !dateRange.toDate}
                      >
                        <i className="fas fa-eraser me-2"></i> Clear
                      </Button>
                      
                      <Button 
                        variant="primary" 
                        className="action-btn apply-btn"
                        onClick={handleApplyDateFilter}
                        disabled={!dateRange.fromDate || !dateRange.toDate || dateError}
                      >
                        <i className="fas fa-check me-2"></i> Apply Filter
                      </Button>
                    </div>
                  </div>
                </div>
              </Collapse>
              
              {(searchQuery || dateRange.fromDate || dateRange.toDate || filters.onlyMissingChannels || filters.nameQuery) && (
                <div className="search-results-info">
                  {isSearching ? (
                    <span><Spinner animation="border" size="sm" /> Searching...</span>
                  ) : (
                    <span>
                      Found {sortedCategories.length} {sortedCategories.length === 1 ? 'category' : 'categories'} 
                      {searchResults.brands?.length > 0 && ` with ${searchResults.brands.length} matching ${searchResults.brands.length === 1 ? 'brand' : 'brands'}`} 
                      {(dateRange.fromDate || dateRange.toDate) && (
                        <span className="date-filter-badge">
                          {" "}in date range 
                          {dateRange.fromDate && <span> from {dateRange.fromDate}</span>}
                          {dateRange.toDate && <span> to {dateRange.toDate}</span>}
                        </span>
                      )}
                      {searchQuery && <span> matching "{searchQuery}"</span>}
                      {sortBy !== 'default' && <span className="sort-indicator"> | Sorted</span>}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={12} className="mb-4">
            <PageStats />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <CategoryCards 
              data={sortedCategories} 
              onCategoryClick={handleCategoryClick}
            />
          </Col>
        </Row>

        {/* Category Detail Modal */}
        {selectedCategory && (
          <CategoryDetail
            show={showCategoryDetail}
            onHide={handleCloseDetail}
            categoryId={selectedCategory.categoryid}
            categoryName={selectedCategory.category_name}
          />
        )}
      </Container>
    </div>
  );
};

export default Dashboard;
